// /en/translate/naati/$destination/$act — NAATI translation × Notary act × Destination country
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
// Phase P-EN-NAATI-ACT-DESTINATION — long-tail SEO for migration/visa applicants
// who need BOTH a notarial act (affidavit, stat dec, jurat) AND NAATI translation
// before submission overseas.
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { NAATI_COUNTRIES, getNaatiCountry } from "@/data/naati-countries";
import { NOTARY_ACTS, getNotaryAct } from "@/data/notary-acts";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode } from "@/lib/programmatic-ratings";
import { speakableSchema, howToSchema, faqPageSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { ContactCTA, DirectAnswer, KeyFactsTable, FaqAccordion } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import NaatiTripleKeyLinks from "@/components/NaatiTripleKeyLinks";
import { ShieldCheck, BadgeCheck, MessageCircle, Phone, Plane, ArrowRight } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

// Curated act allow-list — only acts that pair naturally with NAATI migration workflows.
export const NAATI_ACT_SLUGS = [
  "affidavit",
  "statutory-declaration",
  "jurat",
  "affidavit-single",
  "affidavit-support",
  "certify-translation",
] as const;

export const Route = createFileRoute("/en/translate/naati/$destination/$act")({
  loader: ({ params }) => {
    const c = getNaatiCountry(params.destination);
    const a = getNotaryAct(params.act);
    if (!c || !a) throw notFound();
    if (!NAATI_ACT_SLUGS.includes(params.act as (typeof NAATI_ACT_SLUGS)[number])) throw notFound();
    return { country: params.destination, act: params.act };
  },
  head: ({ params }) => {
    const c = getNaatiCountry(params.destination);
    const a = getNotaryAct(params.act);
    if (!c || !a) return { meta: [{ title: ctaTitle("Not found", "/en/translate/naati/$destination/$act", "en")}] };
    const url = `${SITE}/en/translate/naati/${c.slug}/${a.slug}`;
    const title = `${a.nameEn} + NAATI Translation to ${c.nameEn} — DHL Express ${c.shippingDays} | NYC`;
    const desc = `${a.nameEn} notarised by a Thai Notarial Services Attorney + NAATI-certified translation, couriered to ${c.nameEn} all-in. Accepted by ${c.authorities.slice(0, 2).join(", ")}. Ask us before you file — it's free.`;
    return {
      meta: [
        { title: ctaTitle(title, "/en/translate/naati/$destination/$act", "en") }, { name: "description", content: ctaDesc(desc, "/en/translate/naati/$destination/$act", "en")},
        { property: "og:title", content: ctaTitle(title, "/en/translate/naati/$destination/$act", "en")}, { property: "og:description", content: ctaDesc(desc, "/en/translate/naati/$destination/$act", "en")},
        { property: "og:url", content: url }, { property: "og:locale", content: "en_US" },
        { name: "keywords", content: `${a.nameEn}, NAATI, ${c.nameEn}, notary Thailand, migration translation, ${c.visaTypes.slice(0, 2).join(", ")}` },
      ],
      links: [
        { rel: "canonical", href: url },
        // TH twin exists (Phase P-TH-NAATI-ACT-DESTINATION) — emit symmetric hreflang.
        ...hreflangLinks(
          `/translate/naati/${c.slug}/${a.slug}`,
          `/en/translate/naati/${c.slug}/${a.slug}`,
        ),
      ],
      scripts: [
        howToSchema({
          name: `How to get a ${a.nameEn} + NAATI translation for ${c.nameEn}`,
          description: `Step-by-step combined notarial + NAATI process for ${c.nameEn}`,
          inLanguage: "en-US",
          totalTime: "P3D",
          estimatedCost: { currency: "THB", value: String(a.priceFromTh + 800) },
          steps: [
            { name: `Prepare the ${a.nameEn}`, text: `Bring ${a.required.join(", ")} to our Bangkok HQ or send via courier.` },
            { name: "Notarise with a Thai Notarial Services Attorney", text: `${a.nameEn} executed before a Lawyers Council of Thailand registered Notarial Services Attorney. Same-day in most cases.` },
            { name: "NAATI translation", text: `Translated by a NAATI Certified Translator and stamped — the form ${c.nameEn} authorities require.` },
            { name: `DHL Express to ${c.nameEn}`, text: `Door-to-door DHL Express to ${c.capital} in ${c.shippingDays}. Shipping from THB ${c.shippingFeeThb.toLocaleString()}.` },
          ],
        }),
        faqPageSchema([
          { q: `Do I need both notarisation AND NAATI translation for ${c.nameEn}?`, a: `Yes — ${c.authorities[0]} requires the original ${a.nameEn} to be notarised by a qualified Notary, then translated by a NAATI-certified translator if the source is not in English.` },
          { q: `How long does the combined service take?`, a: `Notarisation is same-day; NAATI translation 1-2 business days; DHL Express to ${c.nameEn} ${c.shippingDays}. End-to-end ~4-7 days.` },
          { q: `Total cost?`, a: `From THB ${a.priceFromTh.toLocaleString()} for the ${a.nameEn} + THB 800/page NAATI translation + DHL THB ${c.shippingFeeThb.toLocaleString()}.` },
          { q: `Is NAATI accepted by ${c.authorities[0]}?`, a: c.notes },
          { q: `Can you draft the ${a.nameEn} for me?`, a: `Yes — our bilingual legal team drafts the ${a.nameEn} in the format required by ${c.nameEn} authorities, then notarises and translates.` },
        ]),
        consolidateGraph([
          speakableSchema(),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
              name: `${a.nameEn} + NAATI Translation to ${c.nameEn}`,
              serviceType: "Combined Notarial Act and NAATI Translation",
              areaServed: { "@type": "Country", name: c.nameEn },
              provider: { "@type": "LegalService", "@id": `${SITE}/#org`, name: "NYC Language Institute", telephone: "+66832494999", url: SITE },
              offers: { "@type": "Offer", price: a.priceFromTh + 800, priceCurrency: "THB", availability: "https://schema.org/InStock" },
              aggregateRating: aggregateRatingNode(url),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList", "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/en` },
                { "@type": "ListItem", position: 2, name: "NAATI Translation", item: `${SITE}/en/translate/naati` },
                { "@type": "ListItem", position: 3, name: c.nameEn, item: `${SITE}/en/translate/naati/online/${c.slug}` },
                { "@type": "ListItem", position: 4, name: a.nameEn, item: url },
              ],
            }),
          },
        ]),
      ],
    };
  },
  component: NaatiActDestinationPage,
});

function NaatiActDestinationPage() {
  const { country, act } = Route.useLoaderData() as { country: string; act: string };
  const c = getNaatiCountry(country)!;
  const a = getNotaryAct(act)!;
  const otherActs = NOTARY_ACTS.filter(
    (x) =>
      x.slug !== a.slug &&
      NAATI_ACT_SLUGS.includes(x.slug as (typeof NAATI_ACT_SLUGS)[number]),
  );
  const nearbyCountries = NAATI_COUNTRIES.filter(
    (x) => x.region === c.region && x.slug !== c.slug,
  ).slice(0, 6);
  const totalFromThb = a.priceFromTh + 800;

  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_55%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/en" className="hover:text-gold">Home</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/en/translate/naati" className="hover:text-gold">NAATI</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/en/translate/naati/online/$country" params={{ country: c.slug }} className="hover:text-gold">{c.nameEn}</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{a.nameEn}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <ShieldCheck className="h-3.5 w-3.5" /> Notarial + NAATI Combined
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight sm:text-5xl">
            {a.nameEn} + NAATI Translation to {c.nameEn}
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            Notarial Services Attorney (Lawyers Council of Thailand) + NAATI Certified Translator,
            couriered DHL Express to {c.capital} in {c.shippingDays}. All-in from THB {totalFromThb.toLocaleString()}.
          </p>
          <div className="mt-6 grid gap-4 lg:grid-cols-[1.1fr_1fr]">
            <DirectAnswer entity={`NYC ${a.nameEn} + NAATI for ${c.nameEn}`}>
              NYC Language Institute pairs a Lawyers-Council-of-Thailand registered Notarial Services Attorney with a NAATI-certified translator so your {a.nameEn} arrives in {c.nameEn} ready for {c.authorities.slice(0, 2).join(" / ")} review. All-in from THB {totalFromThb.toLocaleString()}, DHL Express to {c.capital} in {c.shippingDays}, 99.6% first-time acceptance across.
            </DirectAnswer>
            <KeyFactsTable rows={[
              { k: "Total (from)", v: `THB ${totalFromThb.toLocaleString()}` },
              { k: "Notary fee", v: `THB ${a.priceFromTh.toLocaleString()}` },
              { k: "NAATI", v: "THB 1,300/page" },
              { k: "DHL Express", v: `${c.shippingDays} · THB ${c.shippingFeeThb.toLocaleString()}` },
              { k: "Acceptance", v: c.acceptance },
              { k: "First-time pass", v: "99.6%" },
            ]} />
          </div>
          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md hover:-translate-y-0.5 transition">
              <MessageCircle className="h-4 w-4" /> LINE @nycli
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md hover:-translate-y-0.5 transition">
              <Phone className="h-4 w-4" /> +66 83-249-4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable
          </span>
          {a.nameEn} executed before a Lawyers Council of Thailand Notarial Services Attorney and translated by a NAATI Certified Translator — accepted by {c.authorities.join(", ")} for {c.visaTypes.slice(0, 3).join(", ")}.
        </blockquote>

        {/* Why combined */}
        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            Why {c.nameEn} needs BOTH notarisation and NAATI translation
          </h2>
          <p className="mt-3 text-[15px] leading-relaxed text-slate-700">
            {c.authorities[0]} requires the {a.nameEn} to be sworn before a qualified Notary
            (or Notarial Services Attorney in Thailand) AND, when the underlying document is not
            in English, to be translated by a NAATI-credentialed translator. Skipping either step
            is the #1 reason migration packs are returned. NYC handles both in one workflow:
            the same attorney who notarises also coordinates the NAATI translator, so seals,
            jurat statements and translation certificates line up with {c.nameEn}'s expected format.
          </p>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">Notarial part</div>
              <div className="mt-1 font-display text-sm font-extrabold text-navy-deep">{a.nameEn}</div>
              <ul className="mt-2 list-disc pl-5 text-[13px] text-slate-700">
                {a.useCases.slice(0, 3).map((u) => <li key={u}>{u}</li>)}
              </ul>
              <div className="mt-2 text-[11px] text-slate-500">Required: {a.required.join(", ")}</div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">NAATI part</div>
              <div className="mt-1 font-display text-sm font-extrabold text-navy-deep">NAATI Certified Translation</div>
              <ul className="mt-2 list-disc pl-5 text-[13px] text-slate-700">
                <li>Recognised by {c.authorities.slice(0, 3).join(", ")}</li>
                <li>Stamped NAATI Professional Translator</li>
                <li>Common docs: {c.popularDocs.slice(0, 3).join(", ")}</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Combined process — 4 steps</h2>
          <ol className="mt-5 space-y-3">
            {[
              { t: `Prepare the ${a.nameEn}`, d: `Bring ${a.required.join(", ")}. We can draft the ${a.nameEn} in the format ${c.nameEn} expects.` },
              { t: "Notarise (same day)", d: `${a.nameEn} executed before a Lawyers Council of Thailand registered Notarial Services Attorney. ${a.notes}` },
              { t: "NAATI translation (1-2 days)", d: `Translated and stamped by a NAATI Certified Translator. Translation certificate attached to the notarised original.` },
              { t: `DHL Express to ${c.nameEn}`, d: `Door-to-door to ${c.capital} in ${c.shippingDays}. From THB ${c.shippingFeeThb.toLocaleString()}.` },
            ].map((s, i) => (
              <li key={s.t} className="flex gap-3 rounded-2xl border border-slate-200 bg-white p-4">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 font-display text-sm font-extrabold text-amber-700">{i + 1}</div>
                <div>
                  <div className="font-display text-sm font-extrabold text-navy-deep">{s.t}</div>
                  <p className="mt-1 text-[13px] text-slate-700">{s.d}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section className="mt-12"><ContactCTA /></section>

        {/* FAQ */}
        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">FAQ</h2>
          <div className="mt-5">
            <FaqAccordion items={[
              { q: `Do I need both notarisation AND NAATI translation for ${c.nameEn}?`, a: `Yes — ${c.authorities[0]} requires the original ${a.nameEn} to be notarised and, if not in English, translated by a NAATI-certified translator.` },
              { q: `How long end-to-end?`, a: `Notarisation same-day; NAATI 1-2 business days; DHL Express to ${c.nameEn} ${c.shippingDays}. End-to-end ~4-7 days.` },
              { q: `Total cost?`, a: `From THB ${a.priceFromTh.toLocaleString()} for the ${a.nameEn} + THB 800/page NAATI + DHL THB ${c.shippingFeeThb.toLocaleString()}. All-in from THB ${totalFromThb.toLocaleString()}.` },
              { q: `Will ${c.authorities[0]} accept this?`, a: c.notes },
              { q: `Can NYC draft the ${a.nameEn}?`, a: `Yes — our bilingual legal team drafts in the format ${c.nameEn} authorities expect, then notarises and translates in one workflow.` },
            ]}/>
          </div>
        </section>

        {/* Other acts for this country */}
        {otherActs.length > 0 && (
          <section className="mt-14 border-t border-slate-200 pt-10">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
              Other notarial acts for {c.nameEn} migration
            </h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {otherActs.map((x) => (
                <Link
                  key={x.slug}
                  to="/en/translate/naati/$destination/$act"
                  params={{ destination: c.slug, act: x.slug }}
                  className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
                  data-track-zone="naati-act-dest-cross"
                >
                  <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-700">{x.nameEn}</div>
                  <div className="mt-1 text-[11px] text-slate-500">From THB {(x.priceFromTh + 800).toLocaleString()} all-in</div>
                  <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700">
                    See package <ArrowRight className="h-3 w-3" />
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Nearby countries with this same act */}
        {nearbyCountries.length > 0 && (
          <section className="mt-14 border-t border-slate-200 pt-10">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
              <Plane className="mr-2 inline-block h-6 w-6 text-amber-600" />
              {a.nameEn} for other {c.region} destinations
            </h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {nearbyCountries.map((r) => (
                <Link
                  key={r.slug}
                  to="/en/translate/naati/$destination/$act"
                  params={{ destination: r.slug, act: a.slug }}
                  className="group rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
                  data-track-zone="naati-act-dest-region"
                >
                  <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-700">{r.nameEn}</div>
                  <div className="mt-0.5 text-[11px] text-slate-500">{r.shippingDays} · DHL THB {r.shippingFeeThb.toLocaleString()}</div>
                </Link>
              ))}
            </div>
          </section>
        )}

        <NaatiTripleKeyLinks mode="en" countryNameTh={c.nameTh} countryNameEn={c.nameEn} />
      </div>

      <TrustLayer variant="compact" />
      <QuotableStatsStrip cluster="naati" locale="en" variant="light" />
    </main>
  );
}
